import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { LoginRequest } from '../../interfaces/auth.interface';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit {
  loginForm!: FormGroup;
  isLoading = false;
  errorMessage = '';
  errorType: 'network' | 'auth' | 'server' | 'validation' | 'general' = 'general';
  loginType: 'email' | 'cid' = 'cid';
  showPassword = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    this.loginForm = this.fb.group({
      identifier: ['', [Validators.required]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });

    // Set up validator for identifier based on login type
    this.updateIdentifierValidator();
    
    // Clear error message when user starts typing (but give time to see the error)
    let clearTimeoutId: any;
    this.loginForm.valueChanges.subscribe(() => {
      if (this.errorMessage) {
        clearTimeoutId && clearTimeout(clearTimeoutId);
        clearTimeoutId = setTimeout(() => {
          this.errorMessage = '';
          this.errorType = 'general';
        }, 1000); // Give 1 second to see the error before clearing
      }
    });
  }

  switchLoginType(type: 'email' | 'cid'): void {
    this.loginType = type;
    this.errorMessage = '';
    this.loginForm.get('identifier')?.setValue('');
    this.updateIdentifierValidator();
  }

  private updateIdentifierValidator(): void {
    const identifierControl = this.loginForm.get('identifier');
    if (identifierControl) {
      if (this.loginType === 'email') {
        identifierControl.setValidators([Validators.required, Validators.email]);
      } else {
        identifierControl.setValidators([
          Validators.required,
          Validators.pattern(/^\d{13}$/),
          Validators.minLength(13),
          Validators.maxLength(13)
        ]);
      }
      identifierControl.updateValueAndValidity();
    }
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';
      this.errorType = 'general';

      const formValue = this.loginForm.value;
      const loginData: LoginRequest = {
        password: formValue.password
      };

      // Set either email or cid based on login type
      if (this.loginType === 'email') {
        loginData.email = formValue.identifier;
        // Use staffLogin for email-based login (เจ้าหน้าที่)
        this.authService.staffLogin(loginData).subscribe({
          next: (response) => {
            this.isLoading = false;
            // Redirect is handled by the auth service
          },
          error: (error) => {
            this.isLoading = false;
            this.handleLoginError(error);
          }
        });
      } else {
        loginData.cid = formValue.identifier;
        // Use regular login for CID-based login (ผู้ใช้งาน)
        this.authService.login(loginData).subscribe({
          next: (response) => {
            this.isLoading = false;
            // Redirect is handled by the auth service
          },
          error: (error) => {
            this.isLoading = false;
            this.handleLoginError(error);
          }
        });
      }
    } else {
      this.markFormGroupTouched();
    }
  }

  private handleLoginError(error: any): void {
    console.log('handleLoginError called with:', error);
    
    // Use setTimeout to ensure change detection picks up the changes
    setTimeout(() => {
      // Clear any existing error first
      this.errorMessage = '';
      this.errorType = 'general';
      
      // Set error message based on status
      if (error.status === 401) {
        this.errorType = 'auth';
        this.errorMessage = 'ข้อมูลการเข้าสู่ระบบไม่ถูกต้อง กรุณาตรวจสอบและลองใหม่อีกครั้ง';
      } else if (error.status === 400) {
        this.errorType = 'validation';
        this.errorMessage = 'ข้อมูลที่ส่งมาไม่ถูกต้อง กรุณาตรวจสอบข้อมูลและลองใหม่อีกครั้ง';
      } else if (error.status === 404) {
        this.errorType = 'auth';
        this.errorMessage = 'ไม่พบข้อมูลผู้ใช้ กรุณาตรวจสอบข้อมูลและลองใหม่อีกครั้ง';
      } else if (error.status === 500) {
        this.errorType = 'server';
        this.errorMessage = 'เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ กรุณาลองใหม่อีกครั้งในภายหลัง';
      } else if (error.status === 0) {
        this.errorType = 'network';
        this.errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ตและลองใหม่อีกครั้ง';
      } else if (error.status === 403) {
        this.errorType = 'auth';
        this.errorMessage = 'บัญชีผู้ใช้ถูกระงับ กรุณาติดต่อเจ้าหน้าที่';
      } else {
        // Check if error has a custom message from backend
        const backendMessage = error.error?.message || error.message;
        if (backendMessage) {
          this.errorType = 'general';
          this.errorMessage = `เกิดข้อผิดพลาด: ${backendMessage}`;
        } else {
          this.errorType = 'general';
          this.errorMessage = 'เกิดข้อผิดพลาดที่ไม่คาดคิด กรุณาลองใหม่อีกครั้ง หรือติดต่อเจ้าหน้าที่';
        }
      }
      
      console.log('Error message set to:', this.errorMessage);
      console.log('Error type set to:', this.errorType);
      
      // Force change detection
      this.cdr.detectChanges();
      
      // Auto-clear error message after 15 seconds
      setTimeout(() => {
        if (this.errorMessage) {
          this.errorMessage = '';
          this.errorType = 'general';
          this.cdr.detectChanges();
        }
      }, 15000);
    }, 100);
  }

  private markFormGroupTouched(): void {
    Object.keys(this.loginForm.controls).forEach(key => {
      const control = this.loginForm.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  clearErrorMessage(): void {
    this.errorMessage = '';
    this.errorType = 'general';
  }

  retryLogin(): void {
    this.clearErrorMessage();
    if (this.loginForm.valid) {
      this.onSubmit();
    } else {
      this.markFormGroupTouched();
    }
  }

  // Helper methods for template
  get identifier() {
    return this.loginForm.get('identifier');
  }

  get password() {
    return this.loginForm.get('password');
  }

  get isEmailLogin() {
    return this.loginType === 'email';
  }

  get isCidLogin() {
    return this.loginType === 'cid';
  }

    get isRetryableError() {
    return this.errorType === 'network' || this.errorType === 'server';
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  // Test method to manually trigger error (for debugging)
  testError(): void {
    console.log('Testing error message display...');
    this.errorType = 'auth';
    this.errorMessage = 'ข้อมูลการเข้าสู่ระบบไม่ถูกต้อง กรุณาตรวจสอบและลองใหม่อีกครั้ง';
    console.log('Error message set to:', this.errorMessage);
    this.cdr.detectChanges();
  }

 
} 